import type {
  Channel,
  InboxAssignee,
  InboxChannelGroupId,
  InboxCategory,
  InboxConversationDetail,
  InboxConversationSummary,
  InboxMessage,
  InboxReplyDraft,
  InboxStatus,
  InboxSurface,
  MetaInboxLink,
  SmsKeyword,
} from '@sbc/domain-models/gateway';
import {
  CHANNELS,
  INBOX_CHANNEL_GROUPS,
  QUO_CANNOT_TEXT_NOTE,
  partnershipReplyRefusal,
  quoCanText,
  replyMaxLength,
} from '@sbc/domain-models/gateway';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

export const CHANNEL_NAMES: Record<Channel, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  intercom: 'Intercom',
  slack: 'Slack',
  sbc: 'SBC app',
  tiktok: 'TikTok',
  facebook_group: 'Facebook group',
  quo: 'Quo (SMS)',
  whatsapp: 'WhatsApp',
  klaviyo_sms: 'Klaviyo SMS',
};

export function memberLinked(conversation: Pick<InboxConversationDetail, 'identity'>): boolean {
  const identity = conversation.identity;
  return identity?.status === 'confirmed' && identity.sbcUserId !== null;
}

export function replyLimitNotice(channel: Channel, length: number): string | null {
  const max = replyMaxLength(channel);
  const over = length - max;
  if (over <= 0) {
    return null;
  }
  return `${over} ${over === 1 ? 'character' : 'characters'} over the ${CHANNEL_NAMES[channel]} limit of ${max}. Shorten the reply before you send.`;
}

export function channelFilterOptions(
  connected: readonly Channel[],
  selected: readonly Channel[],
): Channel[] {
  return CHANNELS.filter((channel) => connected.includes(channel) || selected.includes(channel));
}

export const CHANNEL_GROUP_LABELS: Record<InboxChannelGroupId, string> = {
  social: 'Social media',
  whatsapp_white_glove: 'WhatsApp White Glove',
  quo: 'Quo',
  intercom_email: 'Intercom / email',
};

export interface ChannelGroupOption {
  id: InboxChannelGroupId;
  channels: Channel[];
}

export function channelGroupOptions(connected: readonly Channel[]): ChannelGroupOption[] {
  return INBOX_CHANNEL_GROUPS.map((group) => ({
    id: group.id,
    channels: CHANNELS.filter(
      (channel) =>
        group.channels.some((member) => member === channel) && connected.includes(channel),
    ),
  })).filter((group) => group.channels.length > 0);
}

export function groupSelection(
  group: ChannelGroupOption,
  selected: readonly Channel[],
): boolean | 'indeterminate' {
  const picked = group.channels.filter((channel) => selected.includes(channel)).length;
  if (picked === 0) {
    return false;
  }
  return picked === group.channels.length ? true : 'indeterminate';
}

export function toggleGroup(group: ChannelGroupOption, selected: readonly Channel[]): Channel[] {
  const adding = groupSelection(group, selected) !== true;
  return CHANNELS.filter((channel) =>
    group.channels.includes(channel) ? adding : selected.includes(channel),
  );
}

export function toggleChannel(channel: Channel, selected: readonly Channel[]): Channel[] {
  return CHANNELS.filter((candidate) =>
    candidate === channel ? !selected.includes(candidate) : selected.includes(candidate),
  );
}

export function channelFilterLabel(
  selected: readonly Channel[],
  groups: readonly ChannelGroupOption[],
): string {
  const [only] = selected;
  if (only === undefined) {
    return 'Channels';
  }
  if (selected.length === 1) {
    return CHANNEL_NAMES[only];
  }
  const group = groups.find(
    (candidate) =>
      candidate.channels.length === selected.length &&
      candidate.channels.every((channel) => selected.includes(channel)),
  );
  return group ? CHANNEL_GROUP_LABELS[group.id] : `${selected.length} channels`;
}

export const SURFACE_LABELS: Record<InboxSurface, string> = {
  comment_thread: 'Comment',
  dm: 'DM',
};

export const CATEGORY_LABELS: Record<InboxCategory, string> = {
  order_issue: 'Order issue',
  lead: 'Business',
  question: 'Question',
  praise: 'Praise',
  spam: 'Spam',
  other: 'Other',
};

export const STATUS_LABELS: Record<InboxStatus, string> = {
  needs_reply: 'To answer',
  replied: 'Replied',
  dismissed: 'Done',
};

const AVATAR_TONES = [
  'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200',
  'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-200',
] as const;

export function displayName(conversation: InboxConversationSummary): string {
  return conversation.authorUsername ?? conversation.externalThreadRef;
}

export function contactPhone(
  conversation: Pick<InboxConversationSummary, 'channel' | 'externalThreadRef'>,
): string | null {
  return conversation.channel === 'whatsapp' ? `+${conversation.externalThreadRef}` : null;
}

export function replyWindowClosed(
  conversation: Pick<InboxConversationDetail, 'replyWindowEndsAt'>,
  now: Date = new Date(),
): boolean {
  return (
    conversation.replyWindowEndsAt !== null &&
    new Date(conversation.replyWindowEndsAt).getTime() <= now.getTime()
  );
}

export function initialsOf(name: string): string {
  const letters = name
    .replace(/^@/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '');
  return letters.slice(0, 2).join('') || '?';
}

export function avatarTone(name: string): string {
  let hash = 0;
  for (const char of name) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length] ?? AVATAR_TONES[0];
}

export function relativeTime(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const elapsed = now.getTime() - at.getTime();
  if (elapsed < MINUTE_MS) {
    return 'now';
  }
  if (elapsed < HOUR_MS) {
    return `${Math.floor(elapsed / MINUTE_MS)}m`;
  }
  if (elapsed < DAY_MS) {
    return `${Math.floor(elapsed / HOUR_MS)}h`;
  }
  if (elapsed < WEEK_MS) {
    return `${Math.floor(elapsed / DAY_MS)}d`;
  }
  const sameYear = at.getFullYear() === now.getFullYear();
  return at.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function ownerLabel(assignee: InboxAssignee | null): string {
  if (!assignee) {
    return 'Unassigned';
  }
  return assignee.isViewer ? 'You' : shortName(assignee.email);
}

const SENT_ELSEWHERE_LABELS: Partial<Record<Channel, string>> = {
  instagram: 'Sent in Meta Business Suite',
  facebook: 'Sent in Meta Business Suite',
  tiktok: 'Sent in TikTok',
  facebook_group: 'Sent in Facebook',
  quo: 'Sent in Quo',
  klaviyo_sms: 'Sent in Klaviyo',
  intercom: 'Sent in Intercom',
};

export function senderLabel(message: InboxMessage, channel: Channel): string | null {
  if (message.direction !== 'outbound') {
    return null;
  }
  if (message.automated) {
    return 'Sent by automation';
  }
  if (!message.sentByEmail) {
    return SENT_ELSEWHERE_LABELS[channel] ?? 'Sent outside the inbox';
  }
  return `Sent by ${shortName(message.sentByEmail)}`;
}

export function deliveryNote(
  message: Pick<InboxMessage, 'direction' | 'deliveryStatus'>,
  channel: Channel,
): 'Sent' | 'Delivered' | 'Read' | null {
  if (message.deliveryStatus === 'delivered') {
    return 'Delivered';
  }
  if (message.deliveryStatus === 'read') {
    return 'Read';
  }
  if (
    channel === 'quo' &&
    message.direction === 'outbound' &&
    message.deliveryStatus !== 'failed'
  ) {
    return 'Sent';
  }
  return null;
}

export function quoCannotTextNotice(
  conversation: Pick<InboxConversationDetail, 'channel' | 'externalThreadRef' | 'identity'>,
): string | null {
  if (conversation.channel !== 'quo' || quoCanText(conversation.externalThreadRef)) {
    return null;
  }
  const email = conversation.identity?.claimedEmail;
  return email ? `${QUO_CANNOT_TEXT_NOTE}: ${email}` : `${QUO_CANNOT_TEXT_NOTE}.`;
}

// The Intercom drafter keeps one session per thread, so a session id alone does not tell one
// turn's draft from the next.
export function draftPrefillKey(draft: InboxReplyDraft): string {
  return `${draft.sessionId}:${draft.createdAt}`;
}

export function prefilledComposerText(
  current: string,
  previousDraft: string | null,
  draft: string,
): string {
  return !current.trim() || current === previousDraft ? draft : current;
}

export function composerPlaceholder(
  conversation: Pick<InboxConversationDetail, 'channel' | 'surface'>,
): string {
  if (conversation.channel === 'quo') {
    return 'Write a text message… (plain text, sent from the Quo number)';
  }
  if (conversation.channel === 'klaviyo_sms') {
    return 'Text a reply… (plain text, sent from the SBC SMS number)';
  }
  if (conversation.channel === 'intercom') {
    return 'Write a reply… (sent through Intercom to the member’s chat or email)';
  }
  if (conversation.surface !== 'dm') {
    return 'Write a public comment reply…';
  }
  if (conversation.channel === 'whatsapp') {
    return 'Write a WhatsApp reply… (plain text; open for 24h after the last customer message)';
  }
  return 'Write a DM reply… (Meta allows sending within 24h of the last customer message)';
}

function shortName(email: string): string {
  return email.split('@')[0] || email;
}

export const SMS_KEYWORD_LABELS: Record<SmsKeyword, string> = {
  stop: 'Texted STOP. Klaviyo opted them out of SMS.',
  help: 'Texted HELP. Klaviyo sent its help reply.',
  start: 'Texted START. Klaviyo opted them back in to SMS.',
};

export function openOriginalLabel(channel: Channel, surface: InboxSurface): string {
  if (channel === 'intercom') {
    return 'Open in Intercom';
  }
  const name = CHANNEL_NAMES[channel];
  return surface === 'dm' ? `Open profile on ${name}` : `Open post on ${name}`;
}

export function metaInboxLabel(link: MetaInboxLink): string {
  return link.scope === 'thread'
    ? 'Open in Meta Business Suite'
    : 'Open Meta Business Suite inbox (find the chat there)';
}

export interface PartnershipNotice {
  blocked: boolean;
  text: string;
}

export function partnershipNotice(
  conversation: Pick<InboxConversationDetail, 'postOwnerHandle' | 'postOwnerConnected'>,
): PartnershipNotice | null {
  const owner = conversation.postOwnerHandle;
  if (!owner) {
    return null;
  }
  if (!conversation.postOwnerConnected) {
    return { blocked: true, text: partnershipReplyRefusal(owner) };
  }
  return {
    blocked: false,
    text: `This comment is on @${owner}'s partnership ad. Your reply goes out as @${owner}.`,
  };
}

export function closedRepeatsLabel(count: number): string | null {
  if (count === 0) {
    return null;
  }
  return `${count} ${count === 1 ? 'repeat' : 'repeats'} auto-closed`;
}

export function closedRepeatsNote(
  conversation: Pick<InboxConversationSummary, 'closedRepeatCount' | 'authorUsername'>,
): string | null {
  const count = conversation.closedRepeatCount;
  if (count === 0) {
    return null;
  }
  const author = conversation.authorUsername ?? 'This person';
  const times = count === 1 ? 'once more' : `${count} more times`;
  return `${author} posted this same comment ${times}. Those copies were closed automatically, so answering here covers them all.`;
}

export function replyAsHandle(
  conversation: Pick<
    InboxConversationDetail,
    'handle' | 'postOwnerHandle' | 'postOwnerConnected' | 'intercomSender'
  >,
): string {
  if (conversation.intercomSender) {
    return conversation.intercomSender.name ?? 'your Intercom teammate';
  }
  return conversation.postOwnerHandle && conversation.postOwnerConnected
    ? `@${conversation.postOwnerHandle}`
    : conversation.handle;
}

export type ThreadItem =
  | { kind: 'message'; message: InboxMessage }
  | { kind: 'hidden'; messages: InboxMessage[] };

export function groupHiddenMessages(messages: InboxMessage[]): ThreadItem[] {
  const items: ThreadItem[] = [];
  for (const message of messages) {
    const previous = items.at(-1);
    if (message.hideState === null) {
      items.push({ kind: 'message', message });
    } else if (previous?.kind === 'hidden') {
      previous.messages.push(message);
    } else {
      items.push({ kind: 'hidden', messages: [message] });
    }
  }
  return items;
}

export function latestCustomerComment(
  surface: InboxSurface,
  messages: InboxMessage[],
): InboxMessage | null {
  if (surface !== 'comment_thread') {
    return null;
  }
  return (
    messages.findLast((message) => message.direction === 'inbound' && message.deletedAt === null) ??
    null
  );
}
